import { z } from 'zod';
import { UtcDateTimeSchema } from './timestamps.js';
export const MAX_SALE_MONEY_PENCE = 9_000_000_000_000;
export const MAX_SALE_QUANTITY = 1_000_000;
export const PPM_ONE_HUNDRED_PERCENT = 1_000_000;
export const SaleTransactionTypeSchema = z.enum(['SALE', 'REFUND']);
export const HistoricalReceiptTypeSchema = z.enum(['SALE', 'REFUND', 'MIXED', 'ZERO']);
export const TenderTypeSchema = z.enum(['CASH', 'CARD', 'PAYPAL', 'BACS', 'CHEQUE', 'ACCOUNT']);
export const RefundReasonSchema = z.enum(['FAULTY', 'NOT_SUITABLE', 'NO_LONGER_WANTED', 'DEPOSIT_PAID', 'OTHER']);
export const SaleMoneySchema = z.number().int().min(0).max(MAX_SALE_MONEY_PENCE);
export const SignedSaleMoneySchema = z.number().int().min(-MAX_SALE_MONEY_PENCE).max(MAX_SALE_MONEY_PENCE);
export const DiscountRateSchema = z.number().int().min(0).max(PPM_ONE_HUNDRED_PERCENT);
export const CatalogueSaleLineCommandSchema = z.object({
    productId: z.uuid(),
    quantity: z.number().int().min(1).max(MAX_SALE_QUANTITY),
    expectedUnitPriceIncGstPence: SaleMoneySchema,
    itemDiscountRatePpm: DiscountRateSchema.optional().default(0),
    itemDiscountAmountPence: SaleMoneySchema.optional(),
    setUnitPriceIncGstPence: SaleMoneySchema.optional(),
    description: z.string().trim().min(1).max(160).optional(),
}).strict();
export const MiscSaleLineCommandSchema = CatalogueSaleLineCommandSchema.omit({ productId: true }).extend({
    kind: z.literal('MISC'),
    description: z.string().trim().min(1).max(160),
}).strict();
export const SaleLineCommandSchema = z.union([CatalogueSaleLineCommandSchema, MiscSaleLineCommandSchema]).refine(line => [line.itemDiscountRatePpm > 0, line.itemDiscountAmountPence !== undefined, line.setUnitPriceIncGstPence !== undefined].filter(Boolean).length <= 1, 'Choose one item discount mode');
export const SaleQuoteRequestSchema = z.object({
    locationId: z.uuid().optional(),
    customerId: z.uuid().optional(),
    lines: z.array(SaleLineCommandSchema).min(1).max(100),
    basketDiscountRatePpm: DiscountRateSchema.optional().default(0),
    basketDiscountAmountPence: SaleMoneySchema.optional(),
}).strict();
export const SaleTenderCommandSchema = z.object({
    type: TenderTypeSchema,
    amountPence: SaleMoneySchema.refine((value) => value > 0, 'Tender amount must be greater than zero'),
}).strict();
export const ManagementAuthorizationSchema = z.object({
    username: z.string().trim().min(3).max(64).regex(/^[A-Za-z0-9._-]+$/),
    password: z.string().min(1).max(128),
}).strict();
export const CompleteSaleRequestSchema = SaleQuoteRequestSchema.extend({
    operatorUserId: z.uuid().optional(),
    tenders: z.array(SaleTenderCommandSchema).max(5),
    notes: z.string().trim().max(500).nullable().optional(),
    reference: z.string().trim().max(160).nullable().optional(),
}).strict();
export const DeviceInternalBuildRequestSchema = z.object({
    authorization: ManagementAuthorizationSchema,
    command: CompleteSaleRequestSchema,
}).strict();
export const SaleQuoteLineSchema = z.object({
    discountsDisabled: z.boolean().optional(),
    sequence: z.number().int().positive(), productId: z.uuid().nullable(), productName: z.string(), receiptDescription: z.string(),
    productCode: z.string().nullable(), primaryBarcode: z.string().nullable(), quantity: z.number().int(),
    unitPriceIncGstPence: SaleMoneySchema, configuredUnitPriceIncGstPence: SaleMoneySchema.nullable(),
    itemDiscountRatePpm: DiscountRateSchema, lineGrossBeforeDiscountPence: SignedSaleMoneySchema,
    itemDiscountPence: SignedSaleMoneySchema, basketDiscountPence: SignedSaleMoneySchema, customerDiscountPence: SignedSaleMoneySchema,
    totalDiscountPence: SignedSaleMoneySchema, netPence: SignedSaleMoneySchema, gstPence: SignedSaleMoneySchema,
    grossPence: SignedSaleMoneySchema, standardCostExGstUnits: SaleMoneySchema, configuredTaxRatePpm: DiscountRateSchema, taxRatePpm: DiscountRateSchema,
    priceMode: z.enum(['FIXED', 'OPEN']),
    stockTracked: z.boolean(),
    categoryIdSnapshot: z.uuid().nullable().optional(), categoryNameSnapshot: z.string().nullable().optional(),
});
export const SaleQuoteSchema = z.object({
    lines: z.array(SaleQuoteLineSchema), subtotalBeforeDiscountsPence: SignedSaleMoneySchema,
    itemDiscountTotalPence: SignedSaleMoneySchema, basketDiscountTotalPence: SignedSaleMoneySchema,
    customerDiscountTotalPence: SignedSaleMoneySchema, totalDiscountPence: SignedSaleMoneySchema, netTotalPence: SignedSaleMoneySchema,
    gstTotalPence: SignedSaleMoneySchema, grossTotalPence: SignedSaleMoneySchema,
    basketDiscountRatePpm: DiscountRateSchema, customerId: z.uuid().nullable(), customerName: z.string().nullable(), customerAccountReference: z.string().nullable(),
    customerTypeId: z.uuid().nullable(), customerTypeName: z.string().nullable(), customerTypeCode: z.string().nullable(), customerBehavior: z.enum(['STANDARD_DISCOUNT', 'GST_EXEMPT', 'INTERNAL_BUILDS']).nullable(),
    customerDiscountRatePpm: DiscountRateSchema.nullable(), taxTreatment: z.enum(['STANDARD', 'GST_EXEMPT', 'INTERNAL_BUILDS']), accountEnabled: z.boolean(),
});
export const SaleTenderSchema = z.object({
    id: z.uuid(), sequence: z.number().int().positive(), type: TenderTypeSchema,
    amountTenderedPence: SignedSaleMoneySchema, appliedAmountPence: SignedSaleMoneySchema,
    changeDuePence: SignedSaleMoneySchema,
});
export const SaleSummarySchema = z.object({
    id: z.uuid(), saleNumber: z.string(), transactionType: HistoricalReceiptTypeSchema,
    saleAt: UtcDateTimeSchema, grossTotalPence: SignedSaleMoneySchema,
    locationId: z.uuid(), locationCode: z.string(), locationName: z.string(),
    operatorUserId: z.uuid().nullable(), operatorName: z.string().nullable(), tenderSummary: z.string(),
    originalSaleId: z.uuid().nullable(),
    adviceNoteId: z.uuid().nullable(),
    customerId: z.uuid().nullable(), customerName: z.string().nullable(), accountReference: z.string().nullable(), accountSale: z.boolean(), taxTreatment: z.enum(['STANDARD', 'GST_EXEMPT', 'INTERNAL_BUILDS']),
    transactionChannel: z.enum(['TILL', 'BACK_OFFICE']).nullable(), tillSessionId: z.uuid().nullable(),
    provenance: z.enum(['NATIVE_TECHPOS', 'IMPORTED_EPOS_NOW']), sourceReference: z.string().nullable(), sourceOriginalReference: z.string().nullable(),
});
export const SaleDetailSchema = SaleSummarySchema.extend({
    deviceId: z.uuid().nullable(), deviceName: z.string().nullable(), status: z.literal('COMPLETED'),
    subtotalBeforeDiscountsPence: SignedSaleMoneySchema, itemDiscountTotalPence: SignedSaleMoneySchema,
    basketDiscountTotalPence: SignedSaleMoneySchema, customerDiscountTotalPence: SignedSaleMoneySchema, totalDiscountPence: SignedSaleMoneySchema,
    customerTypeId: z.uuid().nullable(), customerTypeName: z.string().nullable(), customerTypeCode: z.string().nullable(), customerBehavior: z.enum(['STANDARD_DISCOUNT', 'GST_EXEMPT', 'INTERNAL_BUILDS']).nullable(), customerDiscountRatePpm: DiscountRateSchema.nullable(),
    netTotalPence: SignedSaleMoneySchema, gstTotalPence: SignedSaleMoneySchema,
    totalAppliedPence: SignedSaleMoneySchema, changeDuePence: SignedSaleMoneySchema,
    notes: z.string().nullable(), reference: z.string().nullable(), createdAt: UtcDateTimeSchema,
    refundReason: RefundReasonSchema.nullable(), refundNote: z.string().nullable(),
    lines: z.array(SaleQuoteLineSchema.extend({ id: z.uuid(), productId: z.uuid().nullable(), configuredUnitPriceIncGstPence: SaleMoneySchema.nullable(), configuredTaxRatePpm: DiscountRateSchema.nullable(), originalSaleLineId: z.uuid().nullable(), restock: z.boolean().nullable(), adviceNoteLineId: z.uuid().nullable(), inventoryPrefulfilled: z.boolean(), historicalCostAvailable: z.boolean(), historicalCostTaxBasis: z.enum(['EX_GST', 'INC_GST', 'UNKNOWN']).nullable().optional(), historicalCategoryAvailable: z.boolean().optional(), refundLinkageStatus: z.enum(['EXACT', 'TRANSACTION_ONLY', 'UNAVAILABLE']).nullable().optional(), sourceRefundReasonId: z.string().nullable().optional(), sourceRefundReason: z.string().nullable().optional() })),
    tenders: z.array(SaleTenderSchema),
});
export const CompleteSaleResponseSchema = z.object({ sale: SaleDetailSchema, replayed: z.boolean() });
// Live read model, deliberately separate from immutable receipt/idempotency snapshots.
export const RefundEligibilityLineSchema = z.object({
    saleLineId: z.uuid(), originalQuantity: z.number().int().positive(),
    refundedQuantity: z.number().int().nonnegative(), remainingRefundableQuantity: z.number().int().nonnegative(),
}).refine(line => line.refundedQuantity <= line.originalQuantity &&
    line.remainingRefundableQuantity === line.originalQuantity - line.refundedQuantity, 'Inconsistent refund quantities');
export const RefundEligibilitySchema = z.object({
    saleId: z.uuid(), locationId: z.uuid(), lines: z.array(RefundEligibilityLineSchema).min(1), fullyRefunded: z.boolean(),
}).refine(value => new Set(value.lines.map(line => line.saleLineId)).size === value.lines.length &&
    value.fullyRefunded === value.lines.every(line => line.remainingRefundableQuantity === 0), 'Inconsistent refund eligibility');
export const RefundLineCommandSchema = z.object({
    saleLineId: z.uuid(), quantity: z.number().int().min(1).max(MAX_SALE_QUANTITY), restock: z.boolean(),
}).strict();
export const RefundSaleRequestSchema = z.object({
    operatorUserId: z.uuid().optional(), lines: z.array(RefundLineCommandSchema).min(1).max(100),
    reason: RefundReasonSchema, note: z.string().trim().max(500).nullable().optional(),
    tenders: z.array(SaleTenderCommandSchema).max(5),
}).strict().superRefine((value, context) => {
    if (value.reason === 'OTHER' && !value.note)
        context.addIssue({ code: 'custom', path: ['note'], message: 'A note is required for OTHER' });
});
export const SaleListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1), pageSize: z.coerce.number().int().min(1).max(100).default(25),
    transactionNumber: z.string().trim().max(64).optional(), type: SaleTransactionTypeSchema.optional(), tender: TenderTypeSchema.optional(),
    locationId: z.uuid().optional(), operatorUserId: z.uuid().optional(), customerId: z.uuid().optional(), search: z.string().trim().max(160).optional(),
    from: z.string().datetime({ offset: true }).optional(), to: z.string().datetime({ offset: true }).optional(),
});
export const SaleListResponseSchema = z.object({ items: z.array(SaleSummarySchema), page: z.number().int(), pageSize: z.number().int(), total: z.number().int() });
export const TillProductSchema = z.object({
    discountsDisabled: z.boolean().optional(),
    id: z.uuid(), name: z.string(), receiptDescription: z.string(), productCode: z.string().nullable(),
    primaryBarcode: z.string().nullable(), unitPriceIncGstPence: SaleMoneySchema.nullable(), taxRatePpm: DiscountRateSchema,
    standardCostExGstUnits: SaleMoneySchema, priceMode: z.enum(['FIXED', 'OPEN']), stockQuantity: z.number().int(),
    stockTracked: z.boolean(),
});
export const TillProductSearchQuerySchema = z.object({ q: z.string().trim().min(1).max(160), limit: z.coerce.number().int().min(1).max(50).default(20) });
export const TillProductSearchResponseSchema = z.object({ items: z.array(TillProductSchema) });
export const TillOperatorSchema = z.object({ id: z.uuid(), displayName: z.string(), role: z.enum(['ADMIN', 'MANAGER', 'TILL']) });
export const TillOperatorListResponseSchema = z.object({ items: z.array(TillOperatorSchema) });
export const ReceiptSchema = z.object({
    merchantName: z.literal('Tech Supplies'), timezone: z.literal('Europe/Jersey'), sale: SaleDetailSchema,
});
